import { dateFormater, ddMMyyyyParse, fromIsoDate, toIsoDate } from '@app/utils/functions';
import { DatePicker, HelperText, HelperTextItem, Label, LabelGroup } from '@patternfly/react-core';
import * as React from 'react';

interface DeliveryDaysFieldProps {
    /** The days as stored, "yyyy-MM-dd". Order does not matter coming in; it is sorted on the way out. */
    value: string[] | undefined | null;
    /** Fired with the full sorted list whenever a day is added or removed. */
    onChange: (days: string[]) => void;
    /** Shows the days alone, with nothing to add or remove them with. */
    isReadOnly?: boolean;
    id?: string;
}

/**
 * The days a class was really taught on, as a list of dates rather than a pattern.
 *
 * A DAY AT A TIME ON PURPOSE. "Alternate Tuesdays" is a description of a schedule, and what the
 * service counts is the days themselves - so the field holds what somebody can read back and check
 * against a calendar, and not a rule nobody can verify.
 *
 * Optional on every class: `deliveredDays` is the field that is usually enough, and this is for what a
 * count cannot answer - a delivery running from December into January is ten days of
 * nobody-knows-which-month, and monthly utilization is the whole question. Where these are present the
 * service treats them as authoritative and does not consult the count.
 */
export const DeliveryDaysField: React.FC<DeliveryDaysFieldProps> = ({
    value, onChange, isReadOnly = false, id = 'deliveryDays'
}) => {
    // The picker is cleared after every pick, so it reads as "add a day" rather than as a field
    // holding one of them. Kept here because DatePicker is otherwise uncontrolled between picks.
    const [pending, setPending] = React.useState<string>('');

    const days = React.useMemo(() => [...(value ?? [])].sort(), [value]);

    const addDay = (date: Date) => {
        // The day the user clicked, not the instant: a Date at local midnight read in any zone but
        // the browser's own would come back as the day before.
        const iso = toIsoDate(date);
        if (!days.includes(iso)) onChange([...days, iso].sort());
        setPending('');
    };

    /**
     * Whether what is in the box is a whole date yet.
     *
     * Load bearing, and not a tidiness check: DatePicker reports a parsed date on every keystroke,
     * and a half typed year parses perfectly well - "13/01/2" is the thirteenth of January in the
     * year 2. Without this, typing one date by hand adds four of them, three of them in antiquity.
     */
    const isWholeDate = (entered: string) => /^\d{2}\/\d{2}\/\d{4}$/.test(entered.trim());

    const removeDay = (iso: string) => onChange(days.filter(day => day !== iso));

    const chips = (
        <LabelGroup
            aria-label="Delivery days"
            numLabels={10}
            isCompact
            // Without this the group collapses to "3 more" on a class taught over a long calendar,
            // which is exactly the class somebody opened this field to read.
            expandedText="Show less"
            collapsedText="${remaining} more"
        >
            {days.map(iso => (
                <Label
                    key={iso}
                    isCompact
                    color="blue"
                    onClose={isReadOnly ? undefined : () => removeDay(iso)}
                    closeBtnAriaLabel={`Remove ${dateFormater(fromIsoDate(iso))}`}
                >
                    {dateFormater(fromIsoDate(iso))}
                </Label>
            ))}
        </LabelGroup>
    );

    if (isReadOnly) {
        return days.length ? chips : <React.Fragment>Not recorded</React.Fragment>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {days.length > 0 && chips}
            <DatePicker
                id={id}
                value={pending}
                placeholder="Add a day"
                dateFormat={dateFormater}
                dateParse={ddMMyyyyParse}
                aria-label="Add a delivery day"
                onChange={(_event, val, date) => {
                    setPending(val);
                    if (isWholeDate(val) && date && !isNaN(date.getTime())) addDay(date);
                }}
                appendTo={() => document.body}
            />
            <HelperText>
                <HelperTextItem variant="indeterminate">
                    {days.length
                        ? `${days.length} ${days.length === 1 ? 'day' : 'days'} recorded. These outrank the delivered days count when utilization is worked out.`
                        : 'Only needed when it matters which days they were - a delivery spaning over multiple weeks with only certain days on each week, say. Otherwise the delivered days count is enough.'}
                </HelperTextItem>
            </HelperText>
        </div>
    );
};

/** The days as a single string, for the places that want plain text rather than markup. */
export const deliveryDaysText = (days: string[] | undefined | null): string =>
    [...(days ?? [])].sort().map(iso => dateFormater(fromIsoDate(iso))).join(', ');
